import { AdsHelpHint } from "@/components/ads-assistant/AdsAssistantCollapsible";
import { adsFeedImportLabelWithHint } from "@/components/ads-assistant/adsAssistantFeedImportLabel";
import type { AdsFeedImportMode, LocationRow } from "@/components/ads-assistant/useAdsAssistantFeedImport";

export function AdsAssistantFeedImportFields(props: {
  embedded: boolean | undefined;
  mode: AdsFeedImportMode;
  setMode: (v: AdsFeedImportMode) => void;
  locationId: string;
  setLocationId: (v: string) => void;
  locationsLoaded: boolean;
  locations: LocationRow[];
  pageId: string;
  setPageId: (v: string) => void;
  canEditMetaToken: boolean;
  overrideToken: string;
  setOverrideToken: (v: string) => void;
  fetchLimit: number;
  setFetchLimit: (v: number) => void;
  daysBack: number;
  setDaysBack: (v: number) => void;
  salesMustPickLocation: boolean;
  pasteJson: string;
  setPasteJson: (v: string) => void;
  salesGhlOnly?: boolean;
}) {
  const {
    embedded,
    mode,
    setMode,
    locationId,
    setLocationId,
    locationsLoaded,
    locations,
    pageId,
    setPageId,
    canEditMetaToken,
    overrideToken,
    setOverrideToken,
    fetchLimit,
    setFetchLimit,
    daysBack,
    setDaysBack,
    salesMustPickLocation,
    pasteJson,
    setPasteJson,
    salesGhlOnly,
  } = props;

  const lb = adsFeedImportLabelWithHint;

  return (
    <div className={(embedded ? "mt-0" : "mt-3") + " grid gap-2 sm:grid-cols-2"}>
      {!salesGhlOnly ? (
      <label className="text-xs text-gray-600 dark:text-gray-400 sm:col-span-2">
        {lb(
          "Mode",
          "What each mode does",
          <>
            <strong>CRM Social Planner (recommended):</strong> Pulls published Facebook and Google posts already
            scheduled in your CRM using the org&apos;s CRM Private Integration key (Admin → CRM). When you pick a location, only
            posts from that location&apos;s connected Facebook Page / Google Business (set in Org Settings) are imported.
            Skips unchanged posts on repeat sync; re-sync can fix location tags if mappings were updated.
            <br />
            <br />
            <strong>Fetch (page token):</strong> This app calls Facebook&apos;s{" "}
            <code className="rounded bg-indigo-100/80 px-0.5 text-[10px] dark:bg-indigo-900/60">published_posts</code> edge
            for the Page ID you enter (or a saved location/org mapping ID when left blank). The Page access token comes from
            your org&apos;s saved secret (or a one-time override if your role allows).
            <br />
            <br />
            <strong>Paste Graph JSON:</strong> Use when you already ran the query elsewhere (Graph API Explorer, Postman,
            your ETL). Paste the full response or an object with a{" "}
            <code className="rounded bg-indigo-100/80 px-0.5 text-[10px] dark:bg-indigo-900/60">data</code> array.
            <br />
            <br />
            <strong>Paste Google local posts:</strong> Paste an array of local post objects, or JSON with{" "}
            <code className="rounded bg-indigo-100/80 px-0.5 text-[10px] dark:bg-indigo-900/60">localPosts</code> /{" "}
            <code className="rounded bg-indigo-100/80 px-0.5 text-[10px] dark:bg-indigo-900/60">local_posts</code> (shape varies
            by export tool—trim to one location&apos;s posts if needed).
            <br />
            <br />
            <strong>Advanced — Direct GBP API:</strong> Live fetch via org service account. Use when CRM sync is unavailable;
            requires Google Business location id on the location or org default in Social mappings.
          </>
        )}
        <select
          className="mt-0.5 w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-zinc-900 dark:text-gray-100"
          value={mode}
          onChange={(e) => setMode(e.target.value as AdsFeedImportMode)}
        >
          <option value="ghl_social_planner">CRM — Social Planner sync</option>
          <option value="facebook_graph_fetch">Facebook — fetch (page token)</option>
          <option value="facebook_graph_paste">Facebook — paste Graph JSON</option>
          <option value="google_local_posts_paste">Google — paste local posts JSON</option>
          <option value="google_gbp_fetch">Google — Advanced direct GBP API</option>
        </select>
      </label>
      ) : null}

      <label className="text-xs text-gray-600 dark:text-gray-400 sm:col-span-2">
        {lb(
          salesMustPickLocation || salesGhlOnly ? "Your location" : "Location (optional)",
          "Tagging imports to an office",
          <>
            Choose an <strong>app location</strong> (lot) for imported rows. Sales must pick an assigned location; trainer+
            may leave untagged for org-wide. CRM API uses the sub-account configured in Admin → CRM; this field stamps{" "}
            <code className="text-[10px]">ads_assistant_social_posts.location_id</code> for filtering and checklist attribution.
          </>
        )}
        <select
          className="mt-0.5 w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-zinc-900 dark:text-gray-100"
          value={locationId}
          onChange={(e) => setLocationId(e.target.value)}
          disabled={!locationsLoaded}
          required={salesMustPickLocation}
        >
          <option value="">{salesMustPickLocation ? "Select location…" : "All locations — do not tag"}</option>
          {locations.map((loc) => (
            <option key={loc.id} value={loc.id}>
              {loc.name || loc.id}
            </option>
          ))}
        </select>
        {!locationsLoaded ? (
          <span className="mt-0.5 block text-[10px] text-gray-500">Loading locations…</span>
        ) : null}
      </label>

      {salesGhlOnly || mode === "ghl_social_planner" || mode === "google_gbp_fetch" ? (
        <>
          <label className="text-xs text-gray-600 dark:text-gray-400">
            {lb(
              "Days back (1–90)",
              "Date window",
              <>
                Only published posts in this window are requested{mode === "google_gbp_fetch" ? " from GBP" : " from CRM"}.
                Default 30 days keeps each sync light.
              </>,
            )}
            <input
              type="number"
              min={1}
              max={90}
              className="mt-0.5 w-28 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-zinc-900 dark:text-gray-100"
              value={daysBack}
              onChange={(e) => setDaysBack(Math.min(90, Math.max(1, Number(e.target.value) || 30)))}
            />
          </label>
          <label className="text-xs text-gray-600 dark:text-gray-400">
            {lb(
              "Max posts (1–100)",
              "Per sync cap",
              <>CRM returns up to this many published posts per request (newest in range).</>,
            )}
            <input
              type="number"
              min={1}
              max={100}
              className="mt-0.5 w-28 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-zinc-900 dark:text-gray-100"
              value={fetchLimit}
              onChange={(e) => setFetchLimit(Math.min(100, Math.max(1, Number(e.target.value) || 25)))}
            />
          </label>
        </>
      ) : mode === "facebook_graph_fetch" ? (
        <>
          <label className="text-xs text-gray-600 dark:text-gray-400">
            {lb(
              "Facebook Page ID (optional)",
              "Where to find the Page ID",
              <>
                This is the <strong>numeric Page id</strong> (or Page id string) Graph uses in paths like{" "}
                <code className="rounded bg-indigo-100/80 px-0.5 text-[10px] dark:bg-indigo-900/60">
                  /&#123;page-id&#125;/published_posts
                </code>
                . Find it in <strong>Meta Business Suite → Settings</strong> for the Page, in the Page&apos;s{" "}
                <strong>About</strong> section (sometimes shown as id), or from Graph API Explorer when you query{" "}
                <code className="rounded bg-indigo-100/80 px-0.5 text-[10px] dark:bg-indigo-900/60">me/accounts</code> with a
                user token that manages the page. It is <em>not</em> the same as @username.
                <br />
                <br />
                If left blank, import tries the selected location&apos;s saved Facebook Page ID first, then the org default.
              </>
            )}
            <input
              className="mt-0.5 w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-zinc-900 dark:text-gray-100"
              value={pageId}
              onChange={(e) => setPageId(e.target.value)}
              placeholder="Leave blank to use saved location/org mapping"
            />
          </label>
          {canEditMetaToken ? (
            <label className="text-xs text-gray-600 dark:text-gray-400">
              {lb(
                "Page token override (this run only)",
                "Optional one-time token",
                <>
                  If set, this token is used only for this import and <strong>is not saved</strong>. Leave empty to use the
                  org&apos;s saved token (recommended for sales workflows once an admin has saved it).
                </>
              )}
              <input
                className="mt-0.5 w-full rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm font-mono dark:border-gray-600 dark:bg-zinc-900 dark:text-gray-100"
                type="password"
                autoComplete="off"
                value={overrideToken}
                onChange={(e) => setOverrideToken(e.target.value)}
                placeholder="Optional; uses saved org token if empty"
              />
            </label>
          ) : null}
          <label className="text-xs text-gray-600 dark:text-gray-400 sm:col-span-2">
            {lb(
              "Max posts (1–100)",
              "Fetch window",
              <>
                Graph returns newest first (for{" "}
                <code className="rounded bg-indigo-100/80 px-0.5 text-[10px] dark:bg-indigo-900/60">published_posts</code>).
                Cap keeps the request small; raise toward 100 if you need a deeper backfill and your token allows it.
              </>
            )}
            <input
              type="number"
              min={1}
              max={100}
              className="mt-0.5 w-28 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-zinc-900 dark:text-gray-100"
              value={fetchLimit}
              onChange={(e) => setFetchLimit(Math.min(100, Math.max(1, Number(e.target.value) || 25)))}
            />
          </label>
        </>
      ) : mode === "facebook_graph_paste" || mode === "google_local_posts_paste" ? (
        <label className="text-xs text-gray-600 dark:text-gray-400 sm:col-span-2">
          <span className="inline-flex flex-wrap items-center gap-1.5">
            {mode === "facebook_graph_paste" ? (
              <>
                JSON — Graph response or <code className="text-[10px]">&#123; data: [...] &#125;</code>
              </>
            ) : (
              <>
                JSON — local posts array or <code className="text-[10px]">&#123; localPosts: [...] &#125;</code>
              </>
            )}
            <AdsHelpHint label={mode === "facebook_graph_paste" ? "Facebook JSON tips" : "Google JSON tips"}>
              {mode === "facebook_graph_paste" ? (
                <>
                  Paste the raw response from{" "}
                  <code className="rounded bg-indigo-100/80 px-0.5 text-[10px] dark:bg-indigo-900/60">
                    GET /&#123;page-id&#125;/published_posts
                  </code>
                  . If your tool already extracted the array, wrap it as{" "}
                  <code className="rounded bg-indigo-100/80 px-0.5 text-[10px] dark:bg-indigo-900/60">
                    &#123; &quot;data&quot;: [ … ] &#125;
                  </code>
                  . Each item should include at least <code className="text-[10px]">id</code> and ideally{" "}
                  <code className="text-[10px]">message</code>, <code className="text-[10px]">created_time</code>,{" "}
                  <code className="text-[10px]">permalink_url</code>, <code className="text-[10px]">full_picture</code>.
                </>
              ) : (
                <>
                  Google&apos;s Business Profile APIs return local posts under location resources; exports from scripts or
                  partner tools differ. This importer accepts a top-level array or an object with{" "}
                  <code className="rounded bg-indigo-100/80 px-0.5 text-[10px] dark:bg-indigo-900/60">localPosts</code> /{" "}
                  <code className="rounded bg-indigo-100/80 px-0.5 text-[10px] dark:bg-indigo-900/60">local_posts</code>. If many
                  rows are skipped, your export may use different field names than this importer expects—try a smaller sample
                  post and adjust keys to match{" "}
                  <span className="whitespace-nowrap">summary / createTime / name</span>-style shapes from the Business
                  Profile API docs.
                </>
              )}
            </AdsHelpHint>
          </span>
          <textarea
            className="mt-0.5 w-full min-h-[140px] rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs font-mono dark:border-gray-600 dark:bg-zinc-900 dark:text-gray-100"
            value={pasteJson}
            onChange={(e) => setPasteJson(e.target.value)}
            spellCheck={false}
          />
        </label>
      ) : null}
    </div>
  );
}
